import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { StorageMap } from '@ngx-pwa/local-storage';
import {
  OrganisationService,
  SoundNotification,
} from '@principle-theorem/ng-principle-shared';
import {
  INotificationSound,
  IPopupNotification,
} from '@principle-theorem/notifications';
import { Staffer } from '@principle-theorem/principle-core';
import { IStaffer } from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  DocumentReference,
  filterUndefined,
  Firestore,
  hardDeleteDoc,
  IReffable,
  orderBy,
  query$,
  resolveWithConcurrency,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import { last } from 'lodash';
import { duration } from 'moment-timezone';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export const POPUP_NOTIFICATION_DEFAULT_SOUND: INotificationSound['value'] =
  'notification.mp3';

export interface IPopupNotificationFormState {
  notifications: WithRef<IPopupNotification>[];
  loading: boolean;
}

const initialState: IPopupNotificationFormState = {
  notifications: [],
  loading: true,
};

@Injectable({
  providedIn: 'root',
})
export class PopupNotificationsService extends ComponentStore<IPopupNotificationFormState> {
  private _maxStackSize = 8;
  private _soundNotification: SoundNotification;
  readonly timeoutMs = duration(10, 'seconds').asMilliseconds();

  readonly notifications$ = this.select((store) =>
    store.notifications.slice(-this._maxStackSize)
  );

  readonly lastNotification$ = this.notifications$.pipe(
    map((notifications) => last(notifications))
  );

  readonly hasMultipleNotifications$ = this.select(
    this.notifications$,
    (notifications) => notifications.length > 1
  );

  readonly loadStafferNotifications = this.effect(
    (staffer$: Observable<IReffable<IStaffer>>) => {
      return staffer$.pipe(
        switchMap((staffer) =>
          query$(
            Staffer.popupNotificationCol(staffer),
            orderBy('createdAt', 'desc')
          )
        ),
        tapResponse(
          (notifications) =>
            this.patchState({
              notifications,
              loading: false,
            }),
          // eslint-disable-next-line no-console
          console.error
        )
      );
    }
  );

  constructor(organisation: OrganisationService, storage: StorageMap) {
    super(initialState);

    this.loadStafferNotifications(
      organisation.staffer$.pipe(filterUndefined())
    );

    this._soundNotification = new SoundNotification(
      'popupNotifications',
      of(POPUP_NOTIFICATION_DEFAULT_SOUND),
      storage,
      this.destroy$
    );
  }

  async addNotification(
    notification: IPopupNotification,
    stafferRef: DocumentReference<IStaffer>
  ): Promise<void> {
    const existingNotification = (await snapshot(this.notifications$)).find(
      (currentNotification) => currentNotification.uid === notification.uid
    );

    if (existingNotification) {
      await Firestore.patchDoc(existingNotification.ref, notification);
      return;
    }

    await Firestore.addDoc(
      Staffer.popupNotificationCol(asReffable(stafferRef)),
      notification
    );
  }

  async playNotificationSound(
    notification: WithRef<IPopupNotification>
  ): Promise<void> {
    if (!notification?.notificationSound) {
      return;
    }

    await this._soundNotification.triggerNotification(
      notification.notificationSound
    );
  }

  async clearAllNotifications(): Promise<void> {
    const notifications = await snapshot(
      this.select((store) => store.notifications)
    );
    await resolveWithConcurrency(notifications, 10, (notification) =>
      hardDeleteDoc(notification.ref)
    );
  }
}
